import React, { useMemo, useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Calculator, History, RotateCcw } from 'lucide-react';

const HISTORY_KEY = 'ledgerly_calculator_history';

const parseStoredHistory = () => {
  try {
    const stored = localStorage.getItem(HISTORY_KEY);
    if (!stored) {
      return [];
    }
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const persistHistory = (history) => {
  localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
};

const evaluateExpression = (expression) => {
  const normalized = expression
    .replace(/×/g, '*')
    .replace(/÷/g, '/');

  if (!/^[0-9+\-*/().%\s]+$/.test(normalized)) {
    throw new Error('Expression invalide');
  }

  const withPercentages = normalized.replace(/(\d+(?:\.\d+)?)%/g, '($1/100)');
  const result = Function(`"use strict"; return (${withPercentages});`)();

  if (typeof result !== 'number' || Number.isNaN(result) || !Number.isFinite(result)) {
    throw new Error('Calcul invalide');
  }

  return Number(result.toFixed(8));
};

const KEYS = [
  { label: 'C', value: 'CLEAR', testId: 'calculator-key-clear' },
  { label: '⌫', value: 'BACKSPACE', testId: 'calculator-key-backspace' },
  { label: '(', value: '(', testId: 'calculator-key-open-parenthesis' },
  { label: ')', value: ')', testId: 'calculator-key-close-parenthesis' },
  { label: '%', value: '%', testId: 'calculator-key-percent' },
  { label: '÷', value: '/', testId: 'calculator-key-divide' },
  { label: '7', value: '7', testId: 'calculator-key-7' },
  { label: '8', value: '8', testId: 'calculator-key-8' },
  { label: '9', value: '9', testId: 'calculator-key-9' },
  { label: '×', value: '*', testId: 'calculator-key-multiply' },
  { label: '4', value: '4', testId: 'calculator-key-4' },
  { label: '5', value: '5', testId: 'calculator-key-5' },
  { label: '6', value: '6', testId: 'calculator-key-6' },
  { label: '-', value: '-', testId: 'calculator-key-minus' },
  { label: '1', value: '1', testId: 'calculator-key-1' },
  { label: '2', value: '2', testId: 'calculator-key-2' },
  { label: '3', value: '3', testId: 'calculator-key-3' },
  { label: '+', value: '+', testId: 'calculator-key-plus' },
  { label: '0', value: '0', testId: 'calculator-key-0' },
  { label: '.', value: '.', testId: 'calculator-key-dot' },
  { label: '=', value: 'EQUALS', testId: 'calculator-key-equals', wide: true }
];

export const CalculatorPage = () => {
  const [expression, setExpression] = useState('');
  const [display, setDisplay] = useState('0');
  const [history, setHistory] = useState(parseStoredHistory());

  const canEvaluate = useMemo(() => expression.trim().length > 0, [expression]);

  const pushHistory = (entry) => {
    const nextHistory = [entry, ...history].slice(0, 30);
    setHistory(nextHistory);
    persistHistory(nextHistory);
  };

  const clearCalculator = () => {
    setExpression('');
    setDisplay('0');
  };

  const clearHistory = () => {
    setHistory([]);
    persistHistory([]);
  };

  const calculateResult = () => {
    if (!canEvaluate) {
      return;
    }

    try {
      const result = evaluateExpression(expression);
      const expressionForHistory = expression;
      const resultAsText = result.toString();
      setExpression(resultAsText);
      setDisplay(resultAsText);
      pushHistory({
        id: `${Date.now()}-${Math.random()}`,
        expression: expressionForHistory,
        result: resultAsText,
        created_at: new Date().toISOString()
      });
    } catch {
      setDisplay('Erreur');
    }
  };

  const appendValue = (value) => {
    if (display === 'Erreur') {
      setExpression('');
      setDisplay('0');
    }

    const nextExpression = `${expression}${value}`;
    setExpression(nextExpression);
    setDisplay(nextExpression || '0');
  };

  const handleKeyPress = (keyValue) => {
    if (keyValue === 'CLEAR') {
      clearCalculator();
      return;
    }

    if (keyValue === 'BACKSPACE') {
      const nextExpression = expression.slice(0, -1);
      setExpression(nextExpression);
      setDisplay(nextExpression || '0');
      return;
    }

    if (keyValue === 'EQUALS') {
      calculateResult();
      return;
    }

    appendValue(keyValue);
  };

  const applyHistoryItem = (item) => {
    setExpression(item.result);
    setDisplay(item.result);
  };

  return (
    <div className="space-y-6" data-testid="calculator-page">
      <div>
        <h1 className="font-outfit text-2xl md:text-3xl font-bold text-slate-900" data-testid="calculator-page-title">Calculatrice</h1>
        <p className="text-slate-500 text-sm md:text-base" data-testid="calculator-page-subtitle">Calcul avancé avec historique réutilisable</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border border-slate-200" data-testid="calculator-main-card">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="font-outfit text-lg flex items-center gap-2" data-testid="calculator-display-title">
              <Calculator className="w-5 h-5 text-[#065F46]" />
              Calcul
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-4">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="calculator-display-card">
              <p className="text-xs text-slate-500 break-all min-h-4" data-testid="calculator-expression-display">{expression || ' '}</p>
              <p className="text-3xl font-mono font-bold text-slate-900 break-all" data-testid="calculator-result-display">{display}</p>
            </div>

            <div className="grid grid-cols-4 gap-2" data-testid="calculator-keypad">
              {KEYS.map((key) => (
                <Button
                  key={key.testId}
                  type="button"
                  variant="outline"
                  className={`h-12 text-base ${key.wide ? 'col-span-2 bg-[#065F46] text-white hover:bg-[#047857]' : ''}`}
                  onClick={() => handleKeyPress(key.value)}
                  data-testid={key.testId}
                >
                  {key.label}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-200" data-testid="calculator-history-card">
          <CardHeader className="border-b border-slate-100">
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="font-outfit text-lg flex items-center gap-2" data-testid="calculator-history-title">
                <History className="w-5 h-5 text-[#065F46]" />
                Historique
              </CardTitle>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={clearHistory}
                data-testid="calculator-history-clear-button"
              >
                <RotateCcw className="w-4 h-4 mr-1" />
                Vider
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-3">
            {history.length === 0 ? (
              <p className="text-sm text-slate-400 py-8 text-center" data-testid="calculator-history-empty">Aucun calcul sauvegardé</p>
            ) : (
              <div className="space-y-2 max-h-[460px] overflow-auto" data-testid="calculator-history-list">
                {history.map((item, index) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => applyHistoryItem(item)}
                    className="w-full text-left border border-slate-200 rounded-md p-2 hover:bg-slate-50 transition-colors"
                    data-testid={`calculator-history-item-${index}`}
                  >
                    <p className="text-xs text-slate-500 break-all" data-testid={`calculator-history-expression-${index}`}>{item.expression}</p>
                    <p className="text-sm font-semibold text-slate-900 break-all" data-testid={`calculator-history-result-${index}`}>= {item.result}</p>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
